import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Alert, Stack, InputAdornment, 
  Breadcrumbs, Link, Chip, Tooltip, Avatar, Checkbox, Menu, MenuItem, Drawer, Divider
} from '@mui/material';
import { 
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, 
  Search as SearchIcon, FilterList,
  Download as DownloadIcon, Timeline as TimelineIcon, 
  EventNote, AssignmentTurnedIn, InfoOutlined,
  CalendarMonth as CalendarIcon, Autorenew
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { format } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';

const ExamsPage = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // UI States
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkAnchorEl, setBulkAnchorEl] = useState<null | HTMLElement>(null);
  
  // Drawer State
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState<any>(null);
  
  const [examSlots, setExamSlots] = useState<any[]>([]);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  const [formData, setFormData] = useState({
    id: '', name: '', examType: 'Mid-Term', term: 'Term 1',
    academicYear: new Date().getFullYear(),
    startDate: '', endDate: '', grades: '',
    description: '', status: 'SCHEDULED'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const res = await axios.get(getApiUrl('exams'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExams(res.data);
      setSelectedIds([]);
    } catch (err: any) {
      console.error('Error loading exams:', err);
      setError(err.response?.data?.message || 'Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const fetchExamSlots = async (examId: string) => {
    try {
      setLoadingSchedule(true);
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const res = await axios.get(getApiUrl(`exam-slots/${examId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExamSlots(res.data);
    } catch (err) {
      console.error('Error fetching exam slots:', err);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const handleAutoPopulate = async (examId: string) => {
    try {
      setLoadingSchedule(true);
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      await axios.post(getApiUrl(`exam-slots/${examId}/auto-populate`), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchExamSlots(examId);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to auto-populate');
    } finally {
      setLoadingSchedule(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      if (formData.id) {
        await axios.put(getApiUrl(`exams/${formData.id}`), formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(getApiUrl('exams'), formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setOpenDialog(false);
      setFormData({ 
          id: '', name: '', examType: 'Mid-Term', term: 'Term 1',
          academicYear: new Date().getFullYear(), startDate: '', endDate: '', 
          grades: '', description: '', status: 'SCHEDULED' 
      });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save exam');
    }
  };

  const handleBulkAction = async (action: 'DELETE' | 'COMPLETED' | 'ONGOING' | 'SCHEDULED') => {
    if (!selectedIds.length) return;
    try {
      setLoading(true);
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      
      if (action === 'DELETE') {
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} exams?`)) return;
        await axios.post(getApiUrl('exams/bulk-delete'), { ids: selectedIds }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(getApiUrl('exams/bulk-status'), { ids: selectedIds, status: action }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Bulk action failed');
    } finally {
      setBulkAnchorEl(null);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) setSelectedIds(filteredExams.map(s => s.id));
    else setSelectedIds([]);
  };

  const handleSelectOne = (id: string) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(i => i !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  const openEditDialog = (exam: any) => {
    setFormData({
      id: exam.id, name: exam.name, examType: exam.examType, term: exam.term,
      academicYear: exam.academicYear, 
      startDate: new Date(exam.startDate).toISOString().split('T')[0],
      endDate: new Date(exam.endDate).toISOString().split('T')[0],
      grades: exam.grades || '', description: exam.description || '', 
      status: exam.status || 'SCHEDULED'
    });
    setOpenDialog(true);
  };

  const openDrawer = (exam: any) => {
    setSelectedExam(exam);
    setDrawerOpen(true);
  };

  const filteredExams = exams.filter(e => 
    e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.examType.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.term.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Chart Data preparation
  const statusDistribution = exams.reduce((acc, curr) => {
    const stat = curr.status || 'SCHEDULED';
    acc[stat] = (acc[stat] || 0) + 1;
    return acc;
  }, {});
  
  const chartData = Object.keys(statusDistribution).map(key => ({
    name: key, count: statusDistribution[key]
  })).sort((a,b) => b.count - a.count);
  
  const getStatusColor = (status: string) => {
      switch(status) {
          case 'COMPLETED': return { bg: '#C6F6D5', text: '#22543D', chart: '#48BB78' };
          case 'ONGOING': return { bg: '#BEE3F8', text: '#2A4365', chart: '#3182CE' };
          case 'SCHEDULED': return { bg: '#FEFCBF', text: '#744210', chart: '#ECC94B' };
          default: return { bg: '#E2E8F0', text: '#4A5568', chart: '#A0AEC0' };
      }
  };

  return (
    <Box sx={{ p: 4, bgcolor: '#F7FAFC', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 1, fontSize: '0.85rem' }}>
          <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer' }}>Dashboard</Link>
          <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard/academics')} sx={{ cursor: 'pointer' }}>Academics</Link>
          <Typography color="text.primary">Examination Control</Typography>
        </Breadcrumbs>
        <Stack direction="row" justifyContent="space-between" alignItems="center flex-wrap gap-2">
          <Box>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#263B7C', letterSpacing: '-0.02em', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
              Examination Control
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Centralized orchestration of tests, grading periods, and academic assessments.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button 
                variant="outlined" 
                startIcon={<DownloadIcon />}
                sx={{ borderRadius: '12px', px: 3, fontWeight: 700, borderColor: '#E2E8F0', color: '#4A5568', bgcolor: 'white' }}
            >
                Export CSV
            </Button>
            <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => {
                  setFormData({ 
                      id: '', name: '', examType: 'Mid-Term', term: 'Term 1',
                      academicYear: new Date().getFullYear(), startDate: '', endDate: '', 
                      grades: '', description: '', status: 'SCHEDULED' 
                  });
                  setOpenDialog(true);
                }}
                sx={{ 
                    bgcolor: '#263B7C', borderRadius: '12px', px: 3,
                    boxShadow: '0 4px 14px rgba(38,59,124,0.25)',
                    '&:hover': { bgcolor: '#1e2e60' }
                }}
            >
                Schedule Exam
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Analytics Ribbon */}
      {!loading && exams.length > 0 && (
         <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', lg: '1fr 2fr' }, 
            gap: 4, mb: 4 
         }}>
             {/* Key Metrics */}
             <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                 <Paper sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #edf2f7' }}>
                     <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Total Assessments</Typography>
                     <Typography variant="h3" fontWeight={800} color="#2D3748" sx={{ mt: 1, mb: 2 }}>{exams.length}</Typography>
                     <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={`${exams.filter(e => e.status === 'ONGOING').length} Ongoing`} size="small" sx={{ bgcolor: '#BEE3F8', color: '#2A4365', fontWeight: 700, borderRadius: '6px' }} />
                        <Chip label={`${exams.filter(e => e.status === 'SCHEDULED').length} Scheduled`} size="small" sx={{ bgcolor: '#FEFCBF', color: '#744210', fontWeight: 700, borderRadius: '6px' }} />
                     </Stack>
                 </Paper>
                 <Paper sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #edf2f7', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                     <AssignmentTurnedIn sx={{ fontSize: 48, color: '#48BB78', opacity: 0.2, position: 'absolute' }} />
                     <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase" zIndex={1}>Completion Rate</Typography>
                     <Typography variant="h3" fontWeight={800} color="#48BB78" sx={{ mt: 1, zIndex: 1 }}>
                         {Math.round((exams.filter(e => e.status === 'COMPLETED').length / exams.length) * 100)}%
                     </Typography>
                 </Paper>
             </Box>
             
             {/* Chart */}
             <Paper sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #edf2f7', display: 'flex', flexDirection: 'column' }}>
                 <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase" sx={{ mb: 1 }}>Assessments by Status</Typography>
                 <Box sx={{ flex: 1, minHeight: 120 }}>
                     <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 30, left: 10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                          <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#718096', fontSize: 12, fontWeight: 600 }} />
                          <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#718096', fontSize: 12, fontWeight: 800 }} width={80} />
                          <RechartsTooltip cursor={{ fill: '#F7FAFC' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                          <Bar dataKey="count" radius={[0, 6, 6, 0]} maxBarSize={24}>
                            {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={getStatusColor(entry.name).chart} />
                            ))}
                          </Bar>
                        </BarChart>
                     </ResponsiveContainer>
                 </Box>
             </Paper>
         </Box>
      )}

      {/* Main Table Area */}
      <Paper sx={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #edf2f7', overflow: 'hidden' }}>
          
        {/* Bulk Action Toolbar */}
        <Box sx={{ p: 2.5, borderBottom: '1px solid #edf2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: selectedIds.length > 0 ? '#EBF4FF' : 'white', transition: 'background-color 0.3s' }}>
            <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  placeholder="Search examinations..."
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ width: 320, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>,
                  }}
                />
                <Button startIcon={<FilterList />} sx={{ color: '#4A5568', fontWeight: 600, borderRadius: '10px' }}>Advanced Filters</Button>
            </Stack>

            {selectedIds.length > 0 && (
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2" fontWeight={800} color="#2B6CB0">
                        {selectedIds.length} exams selected
                    </Typography>
                    <Button 
                        variant="contained" 
                        size="small"
                        onClick={(e) => setBulkAnchorEl(e.currentTarget)}
                        sx={{ bgcolor: '#2B6CB0', borderRadius: '8px', '&:hover': { bgcolor: '#2C5282' } }}
                    >
                        Bulk Actions
                    </Button>
                    <Menu
                        anchorEl={bulkAnchorEl}
                        open={Boolean(bulkAnchorEl)}
                        onClose={() => setBulkAnchorEl(null)}
                        PaperProps={{ elevation: 3, sx: { borderRadius: '12px', minWidth: 160 } }}
                    >
                        <MenuItem onClick={() => handleBulkAction('SCHEDULED')} sx={{ fontWeight: 600, color: '#D69E2E', fontSize: '0.85rem' }}>Set SCHEDULED</MenuItem>
                        <MenuItem onClick={() => handleBulkAction('ONGOING')} sx={{ fontWeight: 600, color: '#3182CE', fontSize: '0.85rem' }}>Set ONGOING</MenuItem>
                        <MenuItem onClick={() => handleBulkAction('COMPLETED')} sx={{ fontWeight: 600, color: '#38A169', fontSize: '0.85rem' }}>Set COMPLETED</MenuItem>
                        <Divider />
                        <MenuItem onClick={() => handleBulkAction('DELETE')} sx={{ fontWeight: 600, color: '#E53E3E', fontSize: '0.85rem' }}>Delete Selected</MenuItem>
                    </Menu>
                </Stack>
            )}
        </Box>

        {error && <Alert severity="error" sx={{ m: 2, borderRadius: '12px' }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#F8F9FA' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                     <Checkbox 
                        checked={filteredExams.length > 0 && selectedIds.length === filteredExams.length}
                        indeterminate={selectedIds.length > 0 && selectedIds.length < filteredExams.length}
                        onChange={handleSelectAll}
                     />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Examination</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Schedule</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Term / Year</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExams.map((exam) => {
                  const sColor = getStatusColor(exam.status || 'SCHEDULED');
                  return (
                  <TableRow 
                    key={exam.id} hover 
                    selected={selectedIds.includes(exam.id)}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer' }}
                    onClick={() => openDrawer(exam)}
                  >
                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                        <Checkbox checked={selectedIds.includes(exam.id)} onChange={() => handleSelectOne(exam.id)} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: `${sColor.chart}15`, color: sColor.chart, width: 40, height: 40, borderRadius: '10px' }}>
                          <EventNote sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={800} color="#2D3748">{exam.name}</Typography>
                          <Chip label={exam.examType} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: '#EDF2F7', color: '#4A5568', mt: 0.5, borderRadius: '4px' }} />
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={800} color="#2D3748">
                          {format(new Date(exam.startDate), 'MMM dd')} - {format(new Date(exam.endDate), 'MMM dd')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          {format(new Date(exam.startDate), 'yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={800} color="#263B7C">{exam.term}</Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Year {exam.academicYear}</Typography>
                    </TableCell>
                    <TableCell align="center">
                       <Chip label={exam.status || 'SCHEDULED'} size="small" sx={{ fontWeight: 800, color: sColor.text, bgcolor: sColor.bg, borderRadius: '8px', fontSize: '0.65rem' }} />
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <Tooltip title="View Details">
                        <IconButton size="small" sx={{ color: '#4A5568' }} onClick={() => openDrawer(exam)}>
                            <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Exam">
                        <IconButton size="small" sx={{ color: '#4C51BF' }} onClick={() => openEditDialog(exam)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Grades Pipeline">
                        <IconButton size="small" sx={{ color: '#38A169' }} onClick={() => navigate('/dashboard/grades')}>
                            <TimelineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )})}
                {filteredExams.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                      <Box sx={{ opacity: 0.5 }}>
                        <EventNote sx={{ fontSize: 48, mb: 1 }} />
                        <Typography variant="body1" fontWeight={600}>No exams found</Typography>
                        <Typography variant="body2">Try adjusting your search filters or schedule a new exam.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Exam Drawer (Detailed View) */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 450 }, p: 0, bgcolor: '#F7FAFC' } }}
      >
        {selectedExam && (
            <Box>
                {/* Drawer Header */}
                <Box sx={{ p: 4, bgcolor: 'white', borderBottom: '1px solid #edf2f7' }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start" mb={2}>
                        <Avatar sx={{ bgcolor: '#4C51BF15', color: '#4C51BF', width: 56, height: 56, borderRadius: '16px' }}>
                           <EventNote sx={{ fontSize: 28 }} />
                        </Avatar>
                        <Box flex={1}>
                            <Typography variant="h5" fontWeight={800} color="#2D3748" sx={{ lineHeight: 1.2 }}>{selectedExam.name}</Typography>
                            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mt: 0.5 }}>{selectedExam.term} • {selectedExam.academicYear}</Typography>
                        </Box>
                        {(() => {
                            const sColor = getStatusColor(selectedExam.status || 'SCHEDULED');
                            return <Chip label={selectedExam.status || 'SCHEDULED'} size="small" sx={{ fontWeight: 800, color: sColor.text, bgcolor: sColor.bg, borderRadius: '8px' }} />;
                        })()}
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Button 
                            fullWidth variant="outlined" 
                            startIcon={<EditIcon />} 
                            onClick={() => { setDrawerOpen(false); openEditDialog(selectedExam); }}
                            sx={{ borderRadius: '10px', fontWeight: 700, borderColor: '#E2E8F0', color: '#4A5568', bgcolor: 'white' }}
                        >
                            Configure
                        </Button>
                        <Button 
                            fullWidth variant="contained" 
                            startIcon={<CalendarIcon />} 
                            onClick={() => {
                                fetchExamSlots(selectedExam.id);
                                setScheduleDialogOpen(true);
                            }}
                            sx={{ borderRadius: '10px', fontWeight: 700, bgcolor: '#263B7C', '&:hover': { bgcolor: '#1e2e60' } }}
                        >
                            Schedule
                        </Button>
                    </Stack>
                </Box>

                {/* Drawer Body */}
                <Box sx={{ p: 4 }}>
                    <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Schedule Details</Typography>
                    
                    <Paper sx={{ p: 3, borderRadius: '16px', mt: 2, border: '1px solid #edf2f7', boxShadow: 'none' }}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={800}>START DATE</Typography>
                                <Typography variant="body1" fontWeight={800} color="#2D3748">{format(new Date(selectedExam.startDate), 'EEEE, MMMM do yyyy')}</Typography>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={800}>END DATE</Typography>
                                <Typography variant="body1" fontWeight={800} color="#2D3748">{format(new Date(selectedExam.endDate), 'EEEE, MMMM do yyyy')}</Typography>
                            </Box>
                        </Stack>
                    </Paper>

                    <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase" sx={{ display: 'block', mt: 4 }}>Specification</Typography>
                    <Paper sx={{ p: 3, borderRadius: '16px', mt: 2, border: '1px solid #edf2f7', boxShadow: 'none' }}>
                         <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                             {selectedExam.description || "No specific instructions or descriptions have been provided for this general examination. Please refer to departmental guidelines."}
                         </Typography>
                         
                         <Box sx={{ mt: 3 }}>
                             <Typography variant="caption" color="text.secondary" fontWeight={800} display="block" mb={1}>APPLICABLE GRADES/LEVELS</Typography>
                             <Stack direction="row" flexWrap="wrap" gap={1}>
                                 {selectedExam.grades ? selectedExam.grades.split(',').map((g: string) => (
                                     <Chip key={g} label={g.trim()} size="small" sx={{ bgcolor: '#EDF2F7', fontWeight: 700, borderRadius: '6px' }} />
                                 )) : <Typography variant="body2" color="text.secondary">All Grades</Typography>}
                             </Stack>
                         </Box>
                    </Paper>

                    <Button 
                        fullWidth 
                        variant="text" 
                        startIcon={<TimelineIcon />} 
                        onClick={() => navigate('/dashboard/grades')}
                        sx={{ mt: 3, borderRadius: '12px', bgcolor: '#38A16915', color: '#38A169', fontWeight: 800, py: 1.5 }}
                    >
                        Access Grading Pipeline
                    </Button>
                </Box>
            </Box>
        )}
      </Drawer>

      {/* Exam Timetable (Slots) Dialog */}
      <Dialog 
        open={scheduleDialogOpen} 
        onClose={() => setScheduleDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '24px', p: 1, minHeight: '60vh' } }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
                <Typography variant="h5" fontWeight={800} color="#263B7C">Exam Timetable</Typography>
                <Typography variant="body2" color="text.secondary">{selectedExam?.name} • Subject Specific Slots</Typography>
            </Box>
            <Button 
                startIcon={<Autorenew />} 
                onClick={() => handleAutoPopulate(selectedExam.id)}
                disabled={loadingSchedule}
                variant="outlined"
                sx={{ borderRadius: '10px', fontWeight: 700 }}
            >
                Auto-populate Subjects
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
           {loadingSchedule ? (
               <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
           ) : (
               <TableContainer sx={{ border: '1px solid #edf2f7', borderRadius: '16px' }}>
                   <Table>
                       <TableHead sx={{ bgcolor: '#F8F9FA' }}>
                           <TableRow>
                               <TableCell sx={{ fontWeight: 800 }}>Subject</TableCell>
                               <TableCell sx={{ fontWeight: 800 }}>Date</TableCell>
                               <TableCell sx={{ fontWeight: 800 }}>Start Time</TableCell>
                               <TableCell sx={{ fontWeight: 800 }}>End Time</TableCell>
                               <TableCell sx={{ fontWeight: 800 }}>Venue</TableCell>
                               <TableCell align="right" sx={{ fontWeight: 800 }}>Actions</TableCell>
                           </TableRow>
                       </TableHead>
                       <TableBody>
                           {examSlots.map((slot) => (
                               <TableRow key={slot.id}>
                                   <TableCell>
                                       <Typography variant="body2" fontWeight={800}>{slot.subject?.name}</Typography>
                                       <Typography variant="caption" color="text.secondary">{slot.subject?.code} • {slot.subject?.grade}</Typography>
                                   </TableCell>
                                   <TableCell>
                                       <TextField 
                                            type="date" size="small" 
                                            value={new Date(slot.date).toISOString().split('T')[0]} 
                                            onChange={async (e) => {
                                                const userInfo = localStorage.getItem('userInfo');
                                                const token = userInfo ? JSON.parse(userInfo).token : null;
                                                await axios.put(getApiUrl(`exam-slots/${slot.id}`), { date: e.target.value }, {
                                                    headers: { Authorization: `Bearer ${token}` }
                                                });
                                                fetchExamSlots(selectedExam.id);
                                            }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                       />
                                   </TableCell>
                                   <TableCell>
                                       <TextField 
                                            type="time" size="small" 
                                            value={slot.startTime} 
                                            onChange={async (e) => {
                                                const userInfo = localStorage.getItem('userInfo');
                                                const token = userInfo ? JSON.parse(userInfo).token : null;
                                                await axios.put(getApiUrl(`exam-slots/${slot.id}`), { startTime: e.target.value }, {
                                                    headers: { Authorization: `Bearer ${token}` }
                                                });
                                                fetchExamSlots(selectedExam.id);
                                            }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                       />
                                   </TableCell>
                                   <TableCell>
                                       <TextField 
                                            type="time" size="small" 
                                            value={slot.endTime} 
                                            onChange={async (e) => {
                                                const userInfo = localStorage.getItem('userInfo');
                                                const token = userInfo ? JSON.parse(userInfo).token : null;
                                                await axios.put(getApiUrl(`exam-slots/${slot.id}`), { endTime: e.target.value }, {
                                                    headers: { Authorization: `Bearer ${token}` }
                                                });
                                                fetchExamSlots(selectedExam.id);
                                            }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                       />
                                   </TableCell>
                                   <TableCell>
                                       <TextField 
                                            placeholder="Venue" size="small" 
                                            value={slot.venue || ''} 
                                            onBlur={async (e) => {
                                                const userInfo = localStorage.getItem('userInfo');
                                                const token = userInfo ? JSON.parse(userInfo).token : null;
                                                await axios.put(getApiUrl(`exam-slots/${slot.id}`), { venue: e.target.value }, {
                                                    headers: { Authorization: `Bearer ${token}` }
                                                });
                                                fetchExamSlots(selectedExam.id);
                                            }}
                                            onChange={(e) => {
                                                const newSlots = examSlots.map(s => s.id === slot.id ? { ...s, venue: e.target.value } : s);
                                                setExamSlots(newSlots);
                                            }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                       />
                                   </TableCell>
                                   <TableCell align="right">
                                       <IconButton color="error" size="small" onClick={async () => {
                                           if (!window.confirm('Remove this subject from the exam?')) return;
                                           const userInfo = localStorage.getItem('userInfo');
                                           const token = userInfo ? JSON.parse(userInfo).token : null;
                                           await axios.delete(getApiUrl(`exam-slots/${slot.id}`), {
                                               headers: { Authorization: `Bearer ${token}` }
                                           });
                                           fetchExamSlots(selectedExam.id);
                                       }}>
                                           <DeleteIcon fontSize="small" />
                                       </IconButton>
                                   </TableCell>
                               </TableRow>
                           ))}
                           {examSlots.length === 0 && (
                               <TableRow>
                                   <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                       No subjects scheduled yet. Use "Auto-populate" to add all subjects.
                                   </TableCell>
                               </TableRow>
                           )}
                       </TableBody>
                   </Table>
               </TableContainer>
           )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setScheduleDialogOpen(false)} variant="contained" sx={{ borderRadius: '10px', px: 4, bgcolor: '#263B7C' }}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Exam Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#263B7C', pb: 1 }}>
            {formData.id ? 'Edit Examination Config' : 'Schedule New Examination'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
              <Box sx={{ gridColumn: 'span 2' }}>
                <TextField 
                  fullWidth label="Examination Name" required placeholder="e.g. End of Year Finals"
                  value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
              <Box>
                <TextField 
                  fullWidth label="Exam Type" required placeholder="e.g. Final, Mid-Term, Mock"
                  value={formData.examType} onChange={e => setFormData({ ...formData, examType: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
              <Box>
                <TextField 
                  select fullWidth label="Status" required
                  value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                >
                    <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                    <MenuItem value="ONGOING">Ongoing</MenuItem>
                    <MenuItem value="COMPLETED">Completed</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </TextField>
              </Box>
              <Box>
                <TextField 
                  fullWidth label="Term" required placeholder="e.g. Term 1"
                  value={formData.term} onChange={e => setFormData({ ...formData, term: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
              <Box>
                <TextField 
                  fullWidth label="Academic Year" type="number" required
                  value={formData.academicYear} onChange={e => setFormData({ ...formData, academicYear: Number(e.target.value) })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
              <Box>
                <TextField 
                  fullWidth label="Start Date" type="date" required InputLabelProps={{ shrink: true }}
                  value={formData.startDate} onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
              <Box>
                <TextField 
                  fullWidth label="End Date" type="date" required InputLabelProps={{ shrink: true }}
                  value={formData.endDate} onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
              <Box sx={{ gridColumn: 'span 2' }}>
                <TextField 
                  fullWidth label="Target Grades/Levels" placeholder="e.g. Grade 1, Grade 2 (Comma separated)"
                  value={formData.grades} onChange={e => setFormData({ ...formData, grades: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
              <Box sx={{ gridColumn: 'span 2' }}>
                <TextField 
                  fullWidth label="Assessment Description & Instructions" multiline rows={3}
                  value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={() => setOpenDialog(false)} sx={{ borderRadius: '10px', fontWeight: 600, color: '#718096' }}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              sx={{ bgcolor: '#263B7C', borderRadius: '10px', px: 4, py: 1.2, fontWeight: 700, '&:hover': { bgcolor: '#1e2e60' } }}
            >
              {formData.id ? 'Update Examination' : 'Confirm Scheduling'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ExamsPage;
